// LTFilterDataType 
export type LTFilterDataType = 'string' | 'number' | 'date' | 'boolean' | 'enum';

export type LTFilterLogic = 'and' | 'or';

export type LTFilter = LTFilterRule | LTFilterGroup;

export interface LTFilterRule {
    field: string;
    operator: LTFilterOperator;     // operator key
    value?: unknown; // value or first value (for between)
    ignoreCase?: boolean;
}

export interface LTFilterGroup {
    logic: LTFilterLogic;
    filters: (LTFilterRule | LTFilterGroup)[];
}

//#region Filter Column

export interface LTFilterColumn {
  field: string;
  title: string;
  type: LTFilterDataType;
  format?: string;
  values?: LTFilterValue[];
  enumValues?: LTEnumValue[]; // Specific for enum types
}

export interface LTFilterValue {
  value: unknown;
  text: string;
}

export interface LTEnumValue {
  key: string | number;
  value: unknown;
  text: string;
  description?: string;
}

//#region Helpers

/**
 * Helper function to create enum column
 * @param field 
 * @param title 
 * @param enumValues 
 * @param format 
 * @returns 
 * @example 
 * createEnumFilterColumn('status', 'Status', [
 *    { key: 'ACTIVE', value: 'ACTIVE', text: 'Active' },
 *    { key: 'INACTIVE', value: 'INACTIVE', text: 'Inactive' }
 * ])
 *
 * createEnumFilterColumn('department', 'Department', createEnumValuesFromEnum(Department, (key) => key.replace(/([A-Z])/g, ' $1')))
 */
export function createEnumFilterColumn(
  field: string,
  title: string,
  enumValues: LTEnumValue[],
  format?: string,
): LTFilterColumn {
  return {
    field,
    title,
    type: 'enum',
    enumValues,
    format,
  };
}

/**
 * Helper function to create enum values from TypeScript enum
 * 
 * @param enumObject 
 * @param textTransform 
 * @returns 
 * @example
 * enum Status { Active = 'ACTIVE', Inactive = 'INACTIVE', Pending = 'PENDING' }
 * const statusEnumValues = createEnumValuesFromEnum(Status, (key, value) => key.toLowerCase());
 */
export function createEnumValuesFromEnum<T extends Record<string, string | number>>(
  enumObject: T,
  textTransform?: (key: string, value: string | number) => string,
): LTEnumValue[] {
  return Object.entries(enumObject)
    .filter(([key, value]) => isNaN(Number(key))) // Filter out numeric keys for string enums
    .map(([key, value]) => ({
      key,
      value,
      text: textTransform ? textTransform(key, value) : key.replace(/([A-Z])/g, ' $1').trim(),
    }));
}

//#endregion Helpers

//#endregion Filter Column

//#region Operator
export type LTFilterOperator = 
  | 'eq' | 'neq' 
  | 'contains' | 'doesnotcontain' 
  | 'startswith' | 'endswith'
  | 'isempty' | 'isnotempty'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'istrue' | 'isfalse' 
  | 'in' | 'notin'
  | 'isnull' | 'isnotnull';
  // | 'between'
  // | 'before' | 'after' | 'datebefore' | 'dateafter'
  // | 'dateis' | 'dateisnot'


export interface LTFilterOperatorDef {
  operator: LTFilterOperator;
  label: string;
  supportedTypes: LTFilterDataType[]; // supported types
  requiresValue: boolean; // none = false, else = true
}

export const LT_FILTER_OPERATORS: LTFilterOperatorDef[] = [
    // string
    { operator: 'contains',         label: 'contains',            requiresValue: true,  supportedTypes: ['string'] },
    { operator: 'doesnotcontain',   label: 'does not contain',    requiresValue: true,  supportedTypes: ['string'] },
    { operator: 'eq',               label: 'equals',              requiresValue: true,  supportedTypes: ['string', 'number', 'date', 'enum'] },
    { operator: 'neq',              label: 'does not equal',      requiresValue: true,  supportedTypes: ['string', 'number', 'date', 'enum'] },
    { operator: 'startswith',       label: 'starts with',         requiresValue: true,  supportedTypes: ['string'] },
    { operator: 'endswith',         label: 'ends with',           requiresValue: true,  supportedTypes: ['string'] },
    { operator: 'isempty',          label: 'is empty',            requiresValue: false, supportedTypes: ['string'] },
    { operator: 'isnotempty',       label: 'is not empty',        requiresValue: false, supportedTypes: ['string'] },

    // number/date
    { operator: 'gt',               label: '>',                   requiresValue: true,  supportedTypes: ['number', 'date'] },
    { operator: 'gte',              label: '≥',                   requiresValue: true,  supportedTypes: ['number', 'date'] },
    { operator: 'lt',               label: '<',                   requiresValue: true,  supportedTypes: ['number', 'date'] },
    { operator: 'lte',              label: '≤',                   requiresValue: true,  supportedTypes: ['number', 'date'] },

    // boolean
    { operator: 'istrue',           label: 'is true',             requiresValue: false, supportedTypes: ['boolean'] },
    { operator: 'isfalse',          label: 'is false',            requiresValue: false, supportedTypes: ['boolean'] },

    // enum
    { operator: 'in',               label: 'in',                  requiresValue: true,  supportedTypes: ['enum'] },
    { operator: 'notin',            label: 'not in',              requiresValue: true,  supportedTypes: ['enum'] },

    // null checks
    { operator: 'isnull',           label: 'is null',             requiresValue: false, supportedTypes: ['string', 'number', 'date', 'enum'] },
    { operator: 'isnotnull',        label: 'is not null',         requiresValue: false, supportedTypes: ['string', 'number', 'date', 'enum'] },

    //{ operator: 'between',       label: 'between',       requiresValue: true, supportedTypes: ['number', 'date'], inputs: 'two' },
];

export function operatorsFor(type: LTFilterDataType) {
  return LT_FILTER_OPERATORS.filter(op => op.supportedTypes.includes(type));
}
//#endregion

export interface FilterChange {
    ast: LTFilterGroup;
}